import { Button } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import React, { useEffect, useState } from 'react';
import ApiClient from '../config/ApiClient';
import { useAppContext } from '../context/AppContext';
import { DailyMoodCheckIn } from '../object-actions/types/types';
import DailyMoodCheckInModal, { MOOD_OPTIONS } from './DailyMoodCheckInModal';

const DailyMoodCheckInButton: React.FC = () => {
    const { mood } = useAppContext();
    const [open, setOpen] = useState(false);
    const [todayCheckIn, setTodayCheckIn] = useState<DailyMoodCheckIn | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchTodayCheckIn = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await ApiClient.get('/api/daily-mood-checkins/today/');
            if (response.success && response.data) {
                setTodayCheckIn(response.data as DailyMoodCheckIn);
            } else {
                setTodayCheckIn(null);
            }
        } catch (err) {
            console.error('Error fetching today\'s check-in:', err);
            setTodayCheckIn(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Update from context if available
        if (mood) {
            // Create a partial check-in object from context mood data
            const checkIn: Partial<DailyMoodCheckIn> = {
                id: mood.id,
                mood_choice: mood.mood_choice as DailyMoodCheckIn['mood_choice'],
                check_in_date: mood.check_in_date,
                created_at: mood.created_at,
                modified_at: mood.modified_at,
                _type: 'DailyMoodCheckIn'
            };
            setTodayCheckIn(checkIn as DailyMoodCheckIn);
        } else {
            // Fetch today's check-in if not in context
            fetchTodayCheckIn();
        }
    }, [mood, fetchTodayCheckIn]);

    const handleSuccess = () => {
        // Refresh the check-in after successful submission
        fetchTodayCheckIn();
    };

    const hasCheckedInToday = todayCheckIn !== null;
    const selectedMood = todayCheckIn?.mood_choice;
    const moodOption = selectedMood ? MOOD_OPTIONS[selectedMood] : null;

    return (
        <>
            <Button
                variant={hasCheckedInToday ? 'contained' : 'outlined'}
                color={hasCheckedInToday ? 'success' : 'primary'}
                onClick={() => setOpen(true)}
                size="small"
                disabled={loading}
                sx={{
                    textTransform: 'none'
                }}
                endIcon={
                    <AvatarGroup
                        sx={{
                            '& .MuiAvatar-root': {
                                width: 18,
                                height: 18
                            }
                        }}
                    >
                        {(Object.entries(MOOD_OPTIONS)).map(([moodKey, option]) => (
                            <Avatar key={moodKey} >
                                {option.emoji}
                            </Avatar>
                        ))}
                    </AvatarGroup>
                }
            >
                How are you feeling today?
            </Button >
            <DailyMoodCheckInModal
                open={open}
                onClose={() => setOpen(false)}
                existingCheckIn={todayCheckIn}
                onSuccess={handleSuccess}
            />
        </>
    );
};

export default DailyMoodCheckInButton;

